import { useFloating, autoUpdate, offset, useDismiss, useHover, useInteractions } from "@floating-ui/react"
import { ButtonHTMLAttributes, DetailedHTMLProps, HTMLAttributes, useEffect, useState } from "react"
import { MIBIcon } from "src/componentsUtils/MIBIcon"
import { MITFloatingPortal } from "src/componentsUtils/PortalHelpers"


type GlassButtonProps = {
   children: React.ReactNode
   title?: string
   "title-placement"?: "top" | "bottom" | "left" | "right"
} & DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>

export function OnMapButton({ title, ...props }: GlassButtonProps) {
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: props["title-placement"] || "bottom",
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });

   const {
      getFloatingProps: getTooltipFloatingProps
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);


   return (
      <>
         <button {...props} ref={refs.setReference} type="button" className={"OnMapButton " + (props.className || "")} >
            {props.children}
         </button>
         {Boolean(title && tooltipOpen) &&
            <MITFloatingPortal>
               <div ref={refs.setFloating} className="OnMapButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
                  {title || "ThisIsToolTIp"}
               </div>
            </MITFloatingPortal>
         }
      </>
   )
}

export function GlassButton({ title, ...props }: GlassButtonProps) {
   const [tooltipOpen, setTooltipOpen] = useState(false);
   const { refs, floatingStyles, context: tooltipContext } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: props["title-placement"],
      open: tooltipOpen,
      middleware: [offset(5)],
      onOpenChange: setTooltipOpen,
   });

   const {
      getFloatingProps: getTooltipFloatingProps
   } = useInteractions([useHover(tooltipContext), useDismiss(tooltipContext)]);


   return (
      <>
         <button {...props} ref={refs.setReference} type="button" className={"GlassButton " + (props.className || "")} >
            {props.children}
         </button>
         {Boolean(title && tooltipOpen) &&
            <MITFloatingPortal>
               <div ref={refs.setFloating} className="GlassButtonToolTip" style={floatingStyles} {...getTooltipFloatingProps()} >
                  {title || "ThisIsToolTIp"}
               </div>
            </MITFloatingPortal>
         }
      </>
   )
}

type GlassButtonDivProps = {
   children: React.ReactNode
} & DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>

export function GlassInfoButton(props: GlassButtonDivProps) {
   return (
      <div {...props} className={"GlassDivButton " + (props.className || "")}>
         {props.children}
      </div>
   )
}

export function GlassButtonRow(props: { children, wrap?, adjust?: "start" | "center" | "end" }) {
   return (
      <div className="GlassButtonRow" style={{ flexWrap: (props.wrap ? "wrap" : "nowrap"), justifyContent: props.adjust || "start" }}>
         {props.children}
      </div>
   )
}


export function GlassButtonDropdown({ children, options, onSelect, ...props }: GlassButtonProps & { options: { text: string, value: any }[], onSelect: (a) => void, placement?: "bottom-start" | "bottom-end" | "top-start" | "top-end" }) {
   const [showOptions, setShowOptions] = useState<boolean>(false)
   const { refs, floatingStyles } = useFloating({
      whileElementsMounted: autoUpdate,
      placement: props.placement || "bottom-end",
   });

   useEffect(() => {
      if (!showOptions) {
         return;
      }

      const handlePointerDown = (event: PointerEvent) => {
         const target = event.target as Node | null;
         if (!target) {
            return;
         }

         const reference = refs.reference.current;
         const floating = refs.floating.current;
         const isWithinReference =
            reference instanceof Element && reference.contains(target);
         const isWithinFloating =
            floating instanceof Element && floating.contains(target);
         if (isWithinReference || isWithinFloating) {
            return;
         }

         setShowOptions(false);
      };

      const handleKeyDown = (event: KeyboardEvent) => {
         if (event.key === "Escape") {
            setShowOptions(false);
         }
      };

      document.addEventListener("pointerdown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
         document.removeEventListener("pointerdown", handlePointerDown);
         document.removeEventListener("keydown", handleKeyDown);
      };
   }, [showOptions, refs]);

   useEffect(() => {
      if (props.disabled) {
         setShowOptions(false);
      }
   }, [props.disabled]);

   return (
      <>
         <button
            type="button"
            className={"GlassButton " + props.className}
            ref={refs.setReference}
            disabled={props.disabled}
            onClick={(e) => {
               props.onClick?.(e);
               if (!e.defaultPrevented && !props.disabled) {
                  setShowOptions((a) => !a);
               }
            }}
         >
            {children}
            <div className="ViewButton-Carret">
               {props.placement?.split?.("-")[0] === "top" ?
                  !showOptions ? <MIBIcon icon="expandUp" /> : <MIBIcon icon="expandUpActive" /> :
                  !showOptions ? <MIBIcon icon="expandDown" /> : <MIBIcon icon="expandDownActive" />
               }
            </div>
         </button>
         {
            showOptions ? (
               <MITFloatingPortal  >
                  <div ref={refs.setFloating} className="ViewButtonDropDown" style={{ zIndex: "1001", ...floatingStyles }} >
                     {options.map((a) => {
                        return (
                           <button key={a.value} onClick={() => { onSelect(a.value); setShowOptions(false) }}>{a.text}</button>
                        )
                     })}
                  </div>
               </MITFloatingPortal>
            ) : null}
      </>
   )
}
